import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { Alert, AlertDescription } from '@/components/ui/alert.tsx'
import { CheckCircle, XCircle } from 'lucide-react'
import { ApiClient } from '@/lib/api/client.ts'

/**
 * EMAIL VERIFICATION SUCCESS PAGE
 *
 * With the BFF architecture, the /api/auth/callback route handles token exchange
 * and redirects here after successful email verification.
 * The user's session is already set via cookies by the callback handler.
 */
export function EmailVerifiedPage() {
  const navigate = useNavigate()
  const [verificationStatus, setVerificationStatus] = useState<
    'checking' | 'success' | 'error' | 'expired'
  >('checking')
  const [errorMessage, setErrorMessage] = useState<string>('')

  // For expired link flow - resending verification
  const [email, setEmail] = useState<string>('')
  const [isResending, setIsResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendError, setResendError] = useState<string>('')

  useEffect(() => {
    checkVerificationStatus()
  }, [])

  const checkVerificationStatus = async () => {
    try {
      // Check for error in URL query params (set by /api/auth/callback on failure)
      const params = new URLSearchParams(window.location.search)
      const error = params.get('error')

      if (error) {
        if (error === 'verification_failed') {
          setVerificationStatus('expired')
          setErrorMessage('Verification link has expired or is invalid')
        } else {
          setVerificationStatus('error')
          setErrorMessage(error)
        }
        return
      }

      // If we got here from /api/auth/callback, the session is set in cookies
      // Sign out immediately (no auto-login after verification)
      await ApiClient.signOut()
      setVerificationStatus('success')

      // Auto-redirect after 3 seconds
      setTimeout(() => {
        navigate('/auth', { replace: true })
      }, 3000)
    } catch (err) {
      console.error('Error checking verification status:', err)
      setVerificationStatus('error')
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to verify email',
      )
    }
  }

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault()
    setResendError('')
    setResendSuccess(false)

    if (!email.trim()) {
      setResendError('Email is required')
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setResendError('Please enter a valid email address')
      return
    }

    setIsResending(true)

    try {
      await ApiClient.resendVerification(email.trim())
      setResendSuccess(true)
    } catch (err) {
      console.error('Resend verification error:', err)
      setResendError(
        err instanceof Error
          ? err.message
          : 'Failed to resend verification email',
      )
    } finally {
      setIsResending(false)
    }
  }

  if (verificationStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Verifying your email...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (verificationStatus === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex flex-col items-center gap-4">
              <CheckCircle className="w-16 h-16 text-green-500" />
              <CardTitle className="text-2xl text-center">
                Email Verified!
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-muted-foreground">
              Your email has been successfully verified.
            </p>
            <p className="text-sm text-muted-foreground">
              You can now sign in with your email and password.
            </p>
            <p className="text-xs text-muted-foreground">
              Redirecting to sign in page in 3 seconds...
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Expired link
  if (verificationStatus === 'expired') {
    if (resendSuccess) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex flex-col items-center gap-4">
                <CheckCircle className="w-16 h-16 text-green-500" />
                <CardTitle className="text-2xl text-center">
                  Verification Email Sent!
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 text-center">
              <p className="text-muted-foreground">
                A new verification email has been sent to your inbox.
              </p>
              <p className="text-sm text-muted-foreground">
                Please check your email and click the verification link.
              </p>
              <Button
                onClick={() => navigate('/auth', { replace: true })}
                className="w-full"
              >
                Go to Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
      )
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex flex-col items-center gap-4">
              <XCircle className="w-16 h-16 text-destructive" />
              <CardTitle className="text-2xl text-center">
                Link Expired
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground text-center">
              {errorMessage || 'Your verification link has expired.'}
            </p>
            <p className="text-sm text-muted-foreground text-center">
              Enter your email to receive a new verification link.
            </p>

            <form onSubmit={handleResendVerification} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isResending}
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>

              {resendError && (
                <Alert variant="destructive">
                  <AlertDescription>{resendError}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={isResending}>
                {isResending ? 'Sending...' : 'Resend Verification Email'}
              </Button>
            </form>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/auth', { replace: true })}
                className="text-sm text-primary hover:underline"
              >
                Already verified? Sign In
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Generic error
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex flex-col items-center gap-4">
            <XCircle className="w-16 h-16 text-destructive" />
            <CardTitle className="text-2xl text-center">
              Verification Failed
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          <p className="text-muted-foreground">
            {errorMessage || 'Unable to verify your email address.'}
          </p>
          <Button
            onClick={() => navigate('/auth', { replace: true })}
            className="w-full"
          >
            Back to Sign In
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
